import { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '../../../../../components/forms/Input';
import { RadioCardGroup } from '../../../../../components/forms/RadioCardGroup';
import { RadioGroup } from '../../../../../components/forms/RadioGroup';
import { MoneyInput } from '../../../../../components/forms/MoneyInput';
import { Button } from '../../../../../components/Button';
import { COLLATERAL_TYPE_OPTIONS } from '../../constants';
import {
  buildCollateralStepSchema,
  CollateralStepValues,
  exceedsLtv,
  ltvCap,
  ltvMessage,
} from '../../schemas';
import {
  CashMeCollateralType,
  ICashMeLimiterConditional,
  ICashMeLimiters,
} from '../../types';
import { AddressFields } from '../AddressFields/AddressFields';
import { WizardShell } from '../WizardShell/WizardShell';
import styles from '../WizardShell/WizardShell.module.css';

interface ICollateralStep {
  limiters: ICashMeLimiters;
  conditional?: ICashMeLimiterConditional;
  loanAmount: number;
  defaultValues: Partial<CollateralStepValues>;
  onBack: () => void;
  onSubmit: (values: CollateralStepValues) => void;
}

export function CollateralStep({
  limiters,
  conditional,
  loanAmount,
  defaultValues,
  onBack,
  onSubmit,
}: ICollateralStep) {
  const schema = useMemo(() => buildCollateralStepSchema(limiters), [limiters]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setFocus,
    setError,
    formState: { errors },
  } = useForm<CollateralStepValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      useMainProponentAddress: false,
      financed: false,
      ...defaultValues,
      address: {
        zipCode: '',
        number: '',
        street: '',
        district: '',
        city: '',
        state: '',
        ...defaultValues.address,
      },
    },
  });

  const financed = watch('financed');
  const collateralType = watch('type');
  const collateralValue = watch('value');
  const capFor = (type: CashMeCollateralType) =>
    ltvCap(limiters, conditional, type);

  const submit = handleSubmit((values) => {
    const cap = capFor(values.type as CashMeCollateralType);

    if (values.value && exceedsLtv(loanAmount, cap, values.value)) {
      setError('value', {
        type: 'manual',
        message: ltvMessage(cap, values.value),
      });
      return;
    }

    onSubmit(values);
  });

  return (
    <WizardShell
      step={1}
      totalSteps={4}
      title="Sobre o imóvel"
      nextTitle="Sobre você"
      helper="Vamos lá! Conte mais sobre o imóvel usado em garantia."
    >
      <form onSubmit={submit} noValidate>
        <h2 className={styles.stepHeading}>Onde está localizado o imóvel?</h2>

        <AddressFields
          prefix="address"
          register={register}
          errors={errors.address}
          setValue={setValue}
          setFocus={setFocus}
        />

        <h2 className={styles.stepHeading}>Qual o tipo de imóvel?</h2>

        <div className={styles.field}>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <RadioCardGroup
                name="type"
                options={COLLATERAL_TYPE_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                errors={errors.type?.message}
              />
            )}
          />
        </div>

        <div className={styles.field}>
          <Controller
            control={control}
            name="value"
            render={({ field }) => (
              <MoneyInput
                name="value"
                label="Qual o valor do imóvel?"
                placeholder="R$ 250.000"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                errors={errors.value?.message}
              />
            )}
          />
          {collateralType && collateralValue && !errors.value && (
            <p
              style={{
                fontSize: 12,
                opacity: 0.6,
                color: 'var(--color-text)',
              }}
            >
              {ltvMessage(
                capFor(collateralType as CashMeCollateralType),
                collateralValue
              )}
            </p>
          )}
        </div>

        <div className={styles.field}>
          <Controller
            control={control}
            name="financed"
            render={({ field }) => (
              <RadioGroup
                name="financed"
                label="O imóvel está quitado?"
                options={[
                  { value: 'false', label: 'Sim, está quitado' },
                  { value: 'true', label: 'Não, ainda é financiado' },
                ]}
                value={String(field.value)}
                onChange={(event) =>
                  field.onChange(event.target.value === 'true')
                }
                errors={errors.financed?.message}
              />
            )}
          />
        </div>

        {financed && (
          <>
            <div className={styles.field}>
              <Input
                type="text"
                label="Banco do financiamento"
                placeholder="Banco"
                autoComplete="off"
                errors={errors.financedBank?.message}
                {...register('financedBank')}
              />
            </div>
            <div className={styles.field}>
              <Controller
                control={control}
                name="financedDebitBalance"
                render={({ field }) => (
                  <MoneyInput
                    name="financedDebitBalance"
                    label="Saldo devedor"
                    placeholder="R$ 0"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    errors={errors.financedDebitBalance?.message}
                  />
                )}
              />
            </div>
          </>
        )}

        <label className={styles.checkbox}>
          <input type="checkbox" {...register('useMainProponentAddress')} />
          Este também é o meu endereço atual.
        </label>

        <div className={styles.footer}>
          <Button type="button" color="flat" text="Voltar" onClick={onBack} />
          <Button type="submit" color="primary" text="Continuar" />
        </div>
      </form>
    </WizardShell>
  );
}
